import { Order, ProductUI } from "../types";
import "dotenv";
import { API_BASE_URL } from "./apiBaseUrl";
import { getOrdersMessengerPayload } from "@/shared/utils/runtimeMessenger";
import { showAppAlert } from "@/shared/utils/appDialog";

interface OrderLineProductRaw {
  ID?: string | number;
  NAME?: string;
  PRICE?: number;
  PRICE1?: number;
  PRICE2?: number;
  DISC_COUNT1?: number;
  DISC_COUNT2?: number;
  WEIGHT_OF_THE_FISH?: number;
  DESCRIPTION?: string;
  ID_GOODSGROUP?: string | number;
  SMALL_PHOTO?: string;
  EXTERN_PHOTO?: string;
  GENER_PHOTO?: string;
  ARTICLE?: string;
  OUTCODE?: string;
  BONUS_FREE?: number;
}

interface OrderResponse {
  ID: string;
  ID_ZAKAZ?: string | null;
  DATE: string;
  STATUS: string;
  PAYMENT_METHOD: string;
  PICKUP?: boolean;
  DELIVERY_ADDRESS?: string;
  DISTRICT_ID?: string | null;
  DISTRICT_NAME?: string | null;
  COMMENT?: string | null;
  PHONE?: string | null;
  CASH_ID?: string | null;
  OUTLET_ID?: string | null;
  OUTLET_ONLY_PICKUP?: boolean;
  TOTAL_AMOUNT: number;
  PAYMENT_URL?: string | null;
  EDITABLE?: boolean;
  CAN_PAY_ONLINE?: boolean;
  items?: Array<{
    product: OrderLineProductRaw;
    QUANTITY: number;
    DISH_LINE_ID?: string | null;
    MIN_QUANTITY?: number | null;
  }>;
}

type CreateOrderLineInput = Order["items"][number] & {
  id?: string;
  price?: number;
  name?: string;
  outcode?: string;
  photo?: string;
  article?: string;
};

export interface UpdateOrderDeliveryInput {
  pickup: boolean;
  deliveryAddress?: string;
  districtId?: string;
  outletId?: string;
  cashId?: string;
  comment?: string;
}

export interface UpdateOrderDistrictInput {
  districtId?: string;
  pickup?: boolean;
  outletId?: string;
}

export interface UpdateOrderItemInput {
  dishLineId: string;
  quantity: number;
}

export interface AddOrderItemInput {
  productId: string;
  quantity: number;
}

export class OrdersApi {
  private baseUrl: string;
  private static readonly CREATE_ORDER_TIMEOUT_MS = 25000;

  constructor() {
    this.baseUrl = API_BASE_URL.replace(/\/+$/, "");
  }

  private getAuthParams(): URLSearchParams {
    const { messenger, user_id: userId, init_data: initData } =
      getOrdersMessengerPayload();
    const params = new URLSearchParams();
    if (messenger !== "web") {
      params.set("messenger", messenger);
    }
    if (userId != null && userId > 0) {
      params.set("user_id", String(userId));
    }
    if (initData) {
      params.set("init_data", initData);
    }
    return params;
  }

  private getAuthBody(): {
    messenger: string;
    user_id: number | null;
    init_data: string;
  } {
    const { messenger, user_id: userId, init_data: initData } =
      getOrdersMessengerPayload();
    return { messenger, user_id: userId, init_data: initData };
  }

  private mapDbStatusToOrderStatus(dbStatus: string): Order["status"] {
    const s = (dbStatus || "").toLowerCase();
    if (s === "paid") return "completed";
    if (s === "pending") return "pending";
    if (s === "processing") return "processing";
    return "cancelled";
  }

  private transformOrder(order: OrderResponse): Order {
    const idZakazRaw =
      order.ID_ZAKAZ != null ? String(order.ID_ZAKAZ).trim() : "";
    return {
      id: order.ID?.toString() || "",
      idZakaz: idZakazRaw !== "" ? idZakazRaw : undefined,
      date: order.DATE || new Date().toISOString(),
      status: this.mapDbStatusToOrderStatus(order.STATUS || "pending"),
      paymentMethod: order.PAYMENT_METHOD || "",
      pickup: Boolean(order.PICKUP),
      deliveryAddress: order.DELIVERY_ADDRESS,
      districtId: order.DISTRICT_ID ?? undefined,
      districtName: order.DISTRICT_NAME ?? undefined,
      comment: order.COMMENT ?? undefined,
      phone: order.PHONE ?? undefined,
      cashId: order.CASH_ID ?? undefined,
      outletId: order.OUTLET_ID ?? undefined,
      outletOnlyPickup: Boolean(order.OUTLET_ONLY_PICKUP),
      items:
        order.items?.map((item) => ({
          product: this.transformOrderProduct(item.product),
          quantity: item.QUANTITY || 0,
          dishLineId: item.DISH_LINE_ID ?? undefined,
          minQuantity:
            item.MIN_QUANTITY != null ? item.MIN_QUANTITY : undefined,
        })) || [],
      totalAmount: order.TOTAL_AMOUNT || 0,
      paymentUrl: order.PAYMENT_URL ?? undefined,
      editable: order.EDITABLE,
      canPayOnline: order.CAN_PAY_ONLINE,
    };
  }

  private transformOrderProduct(product: OrderLineProductRaw): ProductUI {
    return {
      id: product.ID != null ? String(product.ID) : "",
      name: product.NAME || "",
      price: product.PRICE ?? 0,
      price1: product.PRICE1 && product.PRICE1 > 0 ? product.PRICE1 : undefined,
      price2: product.PRICE2 && product.PRICE2 > 0 ? product.PRICE2 : undefined,
      discCount1:
        product.DISC_COUNT1 && product.DISC_COUNT1 > 0
          ? product.DISC_COUNT1
          : undefined,
      discCount2:
        product.DISC_COUNT2 && product.DISC_COUNT2 > 0
          ? product.DISC_COUNT2
          : undefined,
      weightOfTheFish:
        product.WEIGHT_OF_THE_FISH && product.WEIGHT_OF_THE_FISH > 0
          ? product.WEIGHT_OF_THE_FISH
          : undefined,
      description: product.DESCRIPTION || "",
      categoryId:
        product.ID_GOODSGROUP != null ? String(product.ID_GOODSGROUP) : "",
      photo: product.SMALL_PHOTO || product.EXTERN_PHOTO || product.GENER_PHOTO,
      article: product.ARTICLE,
      outcode: product.OUTCODE || "",
      bonusFree: product.BONUS_FREE ?? 0,
    };
  }

  private async parseError(response: Response): Promise<string> {
    const errorData = await response.json().catch(() => ({}));
    return (
      (errorData as { message?: string }).message ||
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  async getOrders(): Promise<Order[]> {
    const params = this.getAuthParams();
    const response = await fetch(`${this.baseUrl}/Orders.php?${params}`);

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map((order: OrderResponse) => this.transformOrder(order));
  }

  async getOrderById(orderId: string): Promise<Order | null> {
    const params = this.getAuthParams();
    params.set("id", orderId);
    const response = await fetch(`${this.baseUrl}/Orders.php?${params}`);

    // 404 (нет заказа / чужой) — null; остальное — throw, чтобы UI не врал «не найден».
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (data.error || !data.ID) {
      return null;
    }
    return this.transformOrder(data as OrderResponse);
  }

  async updateOrderDelivery(
    orderId: string,
    payload: UpdateOrderDeliveryInput
  ): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "update_delivery",
        order_id: orderId,
        pickup: payload.pickup,
        delivery_address: payload.deliveryAddress,
        district_id: payload.districtId,
        outlet_id: payload.outletId,
        cash_id: payload.cashId,
        comment: payload.comment,
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async updateOrderPaymentMethod(
    orderId: string,
    paymentMethod: "yookassa" | "cash"
  ): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "update_payment_method",
        order_id: orderId,
        payment_method: paymentMethod,
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async updateOrderDistrict(
    orderId: string,
    payload: UpdateOrderDistrictInput
  ): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "update_district",
        order_id: orderId,
        district_id: payload.districtId,
        pickup: payload.pickup ?? false,
        outlet_id: payload.outletId,
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async updateOrderItems(
    orderId: string,
    items: UpdateOrderItemInput[]
  ): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "update_items",
        order_id: orderId,
        items: items.map((item) => ({
          dish_line_id: item.dishLineId,
          quantity: item.quantity,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async addOrderItems(
    orderId: string,
    items: AddOrderItemInput[]
  ): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "add_items",
        order_id: orderId,
        items: items.map((item) => ({
          product_id: item.productId,
          quantity: item.quantity,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async cancelOrder(orderId: string): Promise<Order> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "cancel",
        order_id: orderId,
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.order) {
      throw new Error("Invalid response");
    }
    return this.transformOrder(data.order as OrderResponse);
  }

  async payOrder(
    orderId: string
  ): Promise<{ paymentUrl: string; amount: number; reused: boolean }> {
    const response = await fetch(`${this.baseUrl}/Orders.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ...this.getAuthBody(),
        action: "pay",
        order_id: orderId,
      }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const data = await response.json();
    if (!data.payment_url) {
      throw new Error("Не удалось получить ссылку на оплату");
    }

    return {
      paymentUrl: data.payment_url,
      amount: typeof data.amount === "number" ? data.amount : 0,
      reused: Boolean(data.reused),
    };
  }

  async createOrder(
    orderData: Omit<Order, "id" | "date" | "status">
  ): Promise<Order | null> {
    try {
      const { messenger, user_id: userId, init_data: initData } =
        typeof window !== "undefined"
          ? getOrdersMessengerPayload()
          : {
              messenger: "web" as const,
              user_id: null as number | null,
              init_data: "",
            };

      const itemsPayload = orderData.items
        .map((item: CreateOrderLineInput) => {
          if (item.product?.id) {
            return {
              product_id: item.product.id,
              quantity: item.quantity,
              price: item.product.price,
              name: item.product.name,
              outcode: item.product.outcode || item.product.id,
              photo: item.product.photo || "",
              article: item.product.article || "",
            };
          }
          if (item.id) {
            return {
              product_id: item.id,
              quantity: item.quantity,
              price: item.price,
              name: item.name,
              outcode: item.outcode || item.id,
              photo: item.photo || "",
              article: item.article || "",
            };
          }
          console.warn("⚠️ Unknown item structure:", item);
          return null;
        })
        .filter(Boolean);

      if (itemsPayload.length === 0) {
        throw new Error("No valid items in order");
      }

      const requestData = {
        items: itemsPayload,
        payment_method: orderData.paymentMethod,
        cash_id: orderData.cashId,
        outlet_id: orderData.outletId,
        delivery_address: orderData.deliveryAddress,
        district_id: orderData.districtId,
        pickup: Boolean(orderData.pickup),
        total_amount: orderData.totalAmount,
        phone: orderData.phone || "",
        fio: orderData.fullName?.trim() || "",
        messenger: messenger,
        user_id: userId,
        init_data: initData,
        comment: orderData.comment,
      };

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        OrdersApi.CREATE_ORDER_TIMEOUT_MS
      );

      const response = await fetch(`${this.baseUrl}/Orders.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestData),
        signal: controller.signal,
      }).finally(() => window.clearTimeout(timeoutId));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();

      const orderId = result.order_id?.toString?.() ?? String(result.order_id ?? "");
      if (!orderId || orderId === "undefined" || orderId === "null") {
        throw new Error("Сервер не вернул номер заказа. Попробуйте ещё раз.");
      }

      const idZakazRaw =
        result.id_zakaz != null ? String(result.id_zakaz).trim() : "";
      const idZakaz = idZakazRaw !== "" ? idZakazRaw : undefined;

      const districtSyncWarning =
        typeof result.district_sync_warning === "string" &&
        result.district_sync_warning.trim() !== ""
          ? result.district_sync_warning
          : undefined;

      if (result.payment_url) {
        const normalizedAmount =
          typeof result.amount === "number" ? result.amount : orderData.totalAmount;
        return {
          id: orderId,
          idZakaz,
          date: new Date().toISOString(),
          status: "pending",
          paymentMethod: orderData.paymentMethod,
          deliveryAddress: orderData.deliveryAddress,
          districtId: orderData.districtId,
          districtName: orderData.districtName,
          items: orderData.items,
          totalAmount: normalizedAmount,
          paymentUrl: result.payment_url,
          editable: true,
          canPayOnline: orderData.paymentMethod === "yookassa",
          districtSyncWarning,
        };
      }

      return {
        id: orderId,
        idZakaz,
        date: new Date().toISOString(),
        status: result.status || "pending",
        paymentMethod: orderData.paymentMethod,
        deliveryAddress: orderData.deliveryAddress,
        districtId: orderData.districtId,
        districtName: orderData.districtName,
        items: orderData.items,
        totalAmount: orderData.totalAmount,
        editable: true,
        canPayOnline: false,
        districtSyncWarning,
      };
    } catch (error) {
      console.error("❌ Error creating order:", error);
      if (error instanceof DOMException && error.name === "AbortError") {
        void showAppAlert(
          "Сервер долго отвечает. Проверьте интернет и попробуйте еще раз."
        );
        return null;
      }
      void showAppAlert(
        error instanceof Error ? error.message : "Не удалось создать заказ"
      );
      return null;
    }
  }
}

export const ordersApi = new OrdersApi();
